"use client";
import pom from "@/utils/pom";
import { applyDataTestId } from "@/utils/testUtils";
import React, {
  useActionState,
  useEffect,
  useState,
  useTransition,
  useCallback,
} from "react";
import { useCookies } from "react-cookie";
import { useRouter } from "next/navigation";
import CloseIcon from "@/components/_icons/closeIcon";
import { useFormatDuration } from "@/utils/useFormatDuration";
import { Calendar } from "@clover-web-lib-sprout/calendar";
import { DateTime, Duration } from "luxon";
import { ServiceSummaryErrorIcon } from "@/components/_icons/serviceSummaryErrorIcon";
import { AvailableTimeSlots } from "./availableTimeSlots";
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from "@/utils/localStorageUtils";
import { ServiceCartItem } from "@/dto/serviceCartItem";
import { TrashIcon } from "@/components/_icons/trashIcon";
import Image from "next/image";
import { LocalStorageKey } from "@/utils/storageKeys";
import { DrawerModal } from "@/components/modals/modals";
import { useCurrency } from "@/app/providers/currencyContext";
import { usePathname } from "next/navigation";
import { getAvailableAppointmentTimesAction } from "@/app/actions";
import { ServiceTimeSkeleton } from "@/components/loadingSkeleton/loadingSkeleton";
import { useTranslations } from "next-intl";
import useMediaQuery from "@/utils/useMediaQuery";
import ArrowRightIcon from "@/components/_icons/arrowRightIcon";
import DefaultLogo from "@/components/header/defaultLogo.svg";
import { daysOfWeek } from "@/utils/dateTimeUtils";
import { getItemUrl } from "@/utils/itemUrlUtils";
import { modifierDisplay } from "@/utils/modifierDisplayUtil";

export const addToServiceCart = (service: any) => {
  const cartItem: ServiceCartItem = {
    key: service?.cartId,
    value: service,
  };
  const cart = getFromLocalStorage<ServiceCartItem[]>(
    LocalStorageKey.SERVICE_CART,
    []
  );
  cart?.push(cartItem);
  saveToLocalStorage(LocalStorageKey.SERVICE_CART, cart);
  window.dispatchEvent(new StorageEvent("storage", { key: "serviceCart" }));
};

export const getServiceCartItems = (): ServiceCartItem[] => {
  const cart = getFromLocalStorage<ServiceCartItem[]>(
    LocalStorageKey.SERVICE_CART,
    []
  );
  return cart;
};

export const ServiceSummary = ({
  isOpen,
  onClose,
  onRequestClose,
  merchant,
  onTriggerLoginModal,
  merchantPreferences,
  merchantColor,
  setIsCartOpen,
}: any) => {
  const formatDuration = useFormatDuration();

  const [cookies] = useCookies(["session"]);
  const router = useRouter();
  const pathname = usePathname();
  const isMobileScreen = useMediaQuery("(max-width: 640px)");
  const t = useTranslations("ServiceSummary");
  const isCheckoutPage: boolean =
    pathname?.includes("/checkout") ||
    pathname?.includes("/guest-checkout") ||
    false;

  const minAppointmentTime =
    merchantPreferences?.appointmentPref?.minTimeBeforeAppt || "PT1H";
  const minDuration = Duration.fromISO(minAppointmentTime);
  const allDaysEmpty =
    merchant?.businessHours?.hours &&
    Object.values(merchant?.businessHours?.hours).every(
      (day: any) => day.length === 0
    );
  const validBusinessHours =
    merchant?.businessHours?.hours !== undefined && !allDaysEmpty;

  const minTimeGreaterThanOneDay = minDuration.as("days") >= 1;
  const getNextOpenDate = (initialDate: any) => {
    if (validBusinessHours) {
      const businessHours = merchant?.businessHours?.hours;

      let date = DateTime.fromISO(initialDate);
      let dayName = daysOfWeek[date.weekday % 7];
      while (
        !businessHours?.[dayName] ||
        businessHours?.[dayName].length === 0
      ) {
        date = date.plus({ days: 1 });
        dayName = daysOfWeek[date.weekday % 7];
      }

      return date;
    } else {
      return DateTime.fromISO(initialDate);
    }
  };

  const initialDate = minTimeGreaterThanOneDay
    ? DateTime.now().plus(minDuration).toFormat("yyyy-MM-dd")
    : DateTime.now().toFormat("yyyy-MM-dd");
  const adjustedInitialDate =
    getNextOpenDate(initialDate).toFormat("yyyy-MM-dd");

  const adjustedInitialHumanReadableDate = getNextOpenDate(
    initialDate
  ).toLocaleString({ weekday: "long", month: "long", day: "numeric" });

  const [serviceCartItems, setServiceCartItems] = useState(() => {
    return getFromLocalStorage<ServiceCartItem[]>(
      LocalStorageKey.SERVICE_CART,
      []
    );
  });

  const [showServiceLimitError, setShowServiceLimitError] =
    useState<boolean>(false);
  const maxServicesPerAppointment =
    merchantPreferences?.appointmentPref?.bookMultiServiceLimit;

  const [showSelectAppointmentDate, setShowSelectAppointmentDate] =
    useState<boolean>(false);

  const [date, setDate] = useState(adjustedInitialDate);
  const [humanReadableDate, setHumanReadableDate] = useState(
    adjustedInitialHumanReadableDate
  );
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  const { format } = useCurrency();

  const getEmptyServiceHours = () => {
    const daysOfWeek = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    const shortDays: ("Sun" | "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat")[] =
      ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    if (validBusinessHours) {
      const service = merchant.businessHours;

      if (!service || !service.hours) return [];
      const emptyDays: (
        | "Sun"
        | "Mon"
        | "Tue"
        | "Wed"
        | "Thu"
        | "Fri"
        | "Sat"
      )[] = [];
      daysOfWeek.forEach((day, index) => {
        if (!service.hours[day] || service.hours[day].length === 0) {
          emptyDays.push(shortDays[index]);
        }
      });
      return emptyDays;
    } else {
      return [];
    }
  };
  const emptyDays: ("Sun" | "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat")[] =
    getEmptyServiceHours();

  useEffect(() => {
    if (
      serviceCartItems &&
      serviceCartItems.length > maxServicesPerAppointment
    ) {
      setServiceCartItems((prevCartItems: any) => {
        const updatedCartItems = prevCartItems.slice(
          0,
          maxServicesPerAppointment
        );
        return updatedCartItems;
      });
      setShowServiceLimitError(true);
    }
  }, [serviceCartItems, maxServicesPerAppointment]);

  useEffect(() => {
    if (isOpen) {
      const storedCart = getFromLocalStorage<ServiceCartItem[]>(
        LocalStorageKey.SERVICE_CART,
        []
      );
      if (storedCart) {
        setServiceCartItems(storedCart);
      }
      document.body.style.overflow = "hidden";
    } else {
      setShowServiceLimitError(false);
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const [isTransitioning, startTransition] = useTransition();
  const [
    availableTimesState,
    getAvailableTimesAction,
    isAvailableTimesLoading,
  ] = useActionState(getAvailableAppointmentTimesAction, {
    data: null,
    error: null,
  });
  const isLoading = isTransitioning || isAvailableTimesLoading;

  const getAvailableTimes = useCallback(
    (selectedDate: string) => {
      const serviceList = serviceCartItems?.map((service: any, index: any) => ({
        serviceId: service?.value.id,
        sortOrder: index + 1,
        ...(service.value.employee.employeeUuid !== "default" && {
          employeeIds: [service.value.employee.employeeUuid],
        }),
      }));

      if (serviceList.length === 0) return;

      const startDate = DateTime.fromISO(selectedDate, {
        zone: merchant.timezone,
      }).toISO();
      const endDate = DateTime.fromISO(selectedDate, {
        zone: merchant.timezone,
      })
        .endOf("day")
        .toISO();

      startTransition(() => {
        getAvailableTimesAction({
          mid: merchant?.id,
          serviceList: serviceList,
          startDate: startDate,
          endDate: endDate,
        });
      });
    },
    [serviceCartItems, merchant]
  );

  useEffect(() => {
    const shouldFetch =
      (isCheckoutPage || showSelectAppointmentDate) &&
      !availableTimesState.data &&
      !isLoading;

    if (shouldFetch) {
      getAvailableTimes(date);
    }
  }, [
    isCheckoutPage,
    showSelectAppointmentDate,
    availableTimesState.data,
    isLoading,
    date,
  ]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (isMobileScreen) return;
      if (event?.key === "serviceCart") {
        const updatedCart = event?.newValue ? JSON.parse(event.newValue) : [];
        setServiceCartItems(updatedCart);
        if (setIsCartOpen) {
          setIsCartOpen(true);
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [isMobileScreen]);

  const removeFromCart = (event: React.MouseEvent, index: number) => {
    event.stopPropagation();
    const newItems = serviceCartItems?.filter((_: any, i: any) => i !== index);
    saveToLocalStorage(LocalStorageKey.SERVICE_CART, newItems);
    setServiceCartItems(newItems);
  };

  const handleContinueToCheckout = () => {
    const dateTimeString = `${date}T${selectedTime}:00`;
    const dateTimeWithTimezone = DateTime.fromISO(dateTimeString, {
      zone: merchant.timezone,
    });
    const epochTimeInMilliseconds = dateTimeWithTimezone.toMillis();
    const updatedServiceCartItems = serviceCartItems?.map((item: any) => ({
      ...item,
      value: {
        ...item?.value,
        appointmentTime: epochTimeInMilliseconds,
      },
    }));
    saveToLocalStorage(LocalStorageKey.SERVICE_CART, updatedServiceCartItems);
    if (cookies.session) {
      router.push("/checkout");
      onRequestClose();
    } else {
      setShowSelectAppointmentDate(false);
      onRequestClose();
      onTriggerLoginModal(true);
    }
  };

  if (
    !merchant?.businessHours?.hours ||
    Object.values(merchant?.businessHours?.hours).every(
      (day: any) => day.length === 0
    )
  ) {
    return <></>;
  }

  const handleSelectDate = (selectedDate: string) => {
    setDate(selectedDate);
    setHumanReadableDate(
      DateTime.fromISO(selectedDate).toLocaleString({
        weekday: "long",
        month: "long",
        day: "numeric",
      })
    );
    setSelectedTime(null);
    getAvailableTimes(selectedDate);
  };

  const timeslots =
    availableTimesState?.data?.totalAvailableTimeslots?.[0]?.timeslots;

  const handleSelectTime = (time: string) => {
    setSelectedTime(time);
  };

  const maxAppointmentTime =
    merchantPreferences?.appointmentPref?.maxTimeBeforeAppt || "P30D";

  const minDaysToAdd = minDuration.days >= 1 ? minDuration.days : 0;

  const startTimestamp = DateTime.now()
    .minus({ days: 1 })
    .plus({ days: minDaysToAdd })
    .toMillis();
  const endTimestamp = DateTime.now()
    .plus({ days: Duration.fromISO(maxAppointmentTime).days })
    .toMillis();

  const dateBoundaries = {
    startTimestamp,
    endTimestamp,
  };

  const handleServiceItemClick = (item: ServiceCartItem) => {
    const selectedService = item.value;
    router.push(`/services/${getItemUrl(selectedService)}`);
    onRequestClose();
  };

  const handleEditTime = () => {
    const dateTimeString = `${date}T${selectedTime}:00`;
    const dateTimeWithTimezone = DateTime.fromISO(dateTimeString, {
      zone: merchant.timezone,
    });
    const epochTimeInMilliseconds = dateTimeWithTimezone.toMillis();
    const updatedServiceCartItems = serviceCartItems?.map((item: any) => ({
      ...item,
      value: {
        ...item?.value,
        appointmentTime: epochTimeInMilliseconds,
      },
    }));
    saveToLocalStorage(LocalStorageKey.SERVICE_CART, updatedServiceCartItems);
    window.dispatchEvent(
      new StorageEvent("storage", { key: LocalStorageKey.SERVICE_CART })
    );
    onRequestClose();
  };

  return (
    <DrawerModal
      isOpen={isOpen}
      onRequestClose={() => {
        setShowSelectAppointmentDate(false);
        onRequestClose();
      }}
      className="overflow-y-auto"
    >
      {!showSelectAppointmentDate ? (
        <div className="flex flex-col h-full w-[100%]">
          <>
            <div className="flex flex-row justify-between items-start px-4 pt-[15px] pb-[13px] shadow-cartHeader sm:shadow-none sm:px-6 sm:pt-8 sm:pb-4">
              <div>
                <h2 className="text-xl font-medium sm:text-2xl sm:font-bold">
                  {t("appointmentSummary")}
                </h2>
              </div>
              <button onClick={onClose} className="">
                <CloseIcon />
              </button>
            </div>
            <div className="flex flex-row pl-4 items-center pt-6 sm:pt-0">
              <Image
                className="h-[50px]"
                src={merchant?.logo || DefaultLogo.src}
                alt={pom.header.merchantLogo}
                width={50}
                height={50}
              />
              <div className="flex flex-col px-4">
                <p className="font-medium text-[24px]">{merchant.name}</p>
                <p className="text-[15px]">
                  {merchant.address?.address1 +
                    merchant.address?.address2 +
                    ", " +
                    merchant.address?.city +
                    ", " +
                    merchant.address?.state +
                    ", " +
                    merchant.address?.zip}
                </p>
              </div>
            </div>
            {showServiceLimitError && (
              <div className="w-full">
                <div className="mx-5 mt-5 h-[72px] bg-[#FFEDED] rounded-lg">
                  <div className="flex flex-row h-full items-center justify-center">
                    <div className="mx-4">
                      <ServiceSummaryErrorIcon />
                    </div>
                    <p>
                      {t("serviceLimitMessage", {
                        count: maxServicesPerAppointment,
                      })}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="flex-grow overflow-y-auto p-6">
              {serviceCartItems?.map((item: any, index: any) => (
                <div
                  key={index}
                  className="flex flex-col justify-between items-center py-4 border-b cursor-pointer"
                  onClick={() => handleServiceItemClick(item)}
                >
                  <div className="w-full flex flex-row justify-between">
                    <p className="text-lg text-[#373737]">{item.value?.name}</p>
                    <p className="text-[16px] font-medium">
                      {format(item?.value?.total)}
                    </p>
                  </div>
                  <div className="w-full flex flex-row justify-between">
                    <div>
                      {item.value?.serviceDetails?.priceType === "PER_UNIT" && (
                        <p className="text-[15px] text-[#727272]">
                          {item.value?.quantity +
                            " " +
                            item.value?.serviceDetails?.unitName +
                            " at " +
                            format(item.value?.serviceDetails?.price) +
                            " per " +
                            item.value?.serviceDetails?.unitName}
                        </p>
                      )}
                      {item.value?.modifiers?.length > 0 && (
                        <p className="text-[15px] text-[#727272]">
                          {modifierDisplay(item.value?.modifiers)}
                        </p>
                      )}
                      <div className="w-full">
                        <p className="text-[15px] text-[#727272]">
                          With{" "}
                          <span style={{ color: merchantColor }}>
                            {item.value?.employee?.nickName
                              ? item.value?.employee?.nickName
                              : item.value?.employee?.name}
                          </span>
                        </p>
                      </div>
                      <div className="w-full">
                        <p className="text-[15px] text-[#727272]">
                          {item.value?.serviceDetails?.serviceItem &&
                            formatDuration(
                              item.value?.serviceDetails?.serviceItem?.duration
                            )}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={(event: React.MouseEvent) =>
                        removeFromCart(event, index)
                      }
                    >
                      <TrashIcon width={14} height={14} />
                    </button>
                  </div>
                  <div className="w-full flex flex-row justify-between"></div>
                </div>
              ))}
            </div>
            <div className="sm:p-6 sm:pb-8">
              <button
                data-testid={applyDataTestId(
                  pom.services.scheduleAppointmentButton
                )}
                style={{
                  backgroundColor:
                    serviceCartItems?.length == 0 ? "#C7C7C7" : merchantColor,
                }}
                className="bg-black text-white font-bold py-2 px-[18px] sm:px-0 h-16 sm:rounded-md w-full sm:h-14 disabled:bg-[#C7C7C7] flex justify-between sm:justify-center items-center"
                onClick={() => {
                  setShowSelectAppointmentDate(true);
                }}
                disabled={serviceCartItems?.length === 0}
              >
                <p className="text-xl font-medium">
                  {t("scheduleAppointment")}
                </p>
                {isMobileScreen && (
                  <ArrowRightIcon
                    className="sm:hidden"
                    color="white"
                    width={24}
                    height={24.7}
                  />
                )}
              </button>
            </div>
          </>
        </div>
      ) : (
        <div className="flex flex-col h-full w-[100%]">
          <>
            <div className="flex flex-row justify-between items-start pt-[15px] pb-[13px] shadow-cartHeader sm:shadow-none sm:pb-0 px-4 sm:px-6 sm:pt-8">
              <div>
                <h2 className="text-xl font-medium sm:text-2xl sm:font-bold">
                  {t("selectDate")}
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowSelectAppointmentDate(false);
                  onRequestClose();
                }}
                className="text-gray-600 hover:text-gray-900"
              >
                <CloseIcon />
              </button>
            </div>
            <div className="flex-grow overflow-y-auto p-6">
              <div>
                <style>{`
                  button[class*='CalendarTableStyles_module_selected'] {
                    background-color: ${merchantColor}!important;
                    box-shadow: none!important;
                  }
                `}</style>
                <Calendar
                  calendarId={"calendar"}
                  date={date}
                  dateBoundaries={dateBoundaries}
                  disableDates={[]}
                  disableRecurringDays={emptyDays}
                  disablePastDates={true}
                  enableFutureDates={true}
                  handleSelectDate={handleSelectDate}
                  isCalendarOpen={true}
                  isModal={true}
                  summaryHour={0}
                  timezone={merchant.timezone}
                />
              </div>
              <div>
                <h3 className="font-medium text-base pb-4">
                  {t("availabilityFor", { date: humanReadableDate })}
                </h3>
                {isLoading && <ServiceTimeSkeleton />}
                {!isLoading && (
                  <AvailableTimeSlots
                    availableTimeSlots={timeslots}
                    selectedTime={selectedTime}
                    handleSelectedTime={handleSelectTime}
                    merchantColor={merchantColor}
                  />
                )}
              </div>
            </div>
            {!isCheckoutPage && (
              <div className="sm:px-6 sm:pb-8">
                <button
                  data-testid={applyDataTestId(
                    pom.services.serviceSummaryContinue
                  )}
                  style={{
                    backgroundColor:
                      serviceCartItems?.length == 0 || selectedTime == null
                        ? "#C7C7C7"
                        : merchantColor,
                  }}
                  className="bg-black text-white font-bold py-2 px-[18px] sm:px-0 sm:rounded-md w-full h-16 sm:h-14 disabled:bg-[#C7C7C7] sm:justify-center items-center justify-between flex"
                  onClick={handleContinueToCheckout}
                  disabled={
                    serviceCartItems?.length == 0 || selectedTime == null
                  }
                >
                  <p className="text-xl font-medium">
                    {t("continueToCheckout")}
                  </p>
                  {isMobileScreen && (
                    <ArrowRightIcon
                      className="sm:hidden"
                      color="white"
                      width={24}
                      height={24.7}
                    />
                  )}
                </button>
              </div>
            )}
            {isCheckoutPage && (
              <div className="px-6 pb-8">
                <button
                  data-testid={applyDataTestId(
                    pom.services.serviceSummaryContinue
                  )}
                  style={{
                    backgroundColor:
                      serviceCartItems?.length == 0 || selectedTime == null
                        ? "#C7C7C7"
                        : merchantColor,
                  }}
                  className="bg-black text-white font-bold py-2 rounded-md w-full h-14 disabled:bg-[#C7C7C7]"
                  onClick={handleEditTime}
                  disabled={
                    serviceCartItems?.length == 0 || selectedTime == null
                  }
                >
                  <p className="text-xl font-medium">{t("confirm")}</p>
                </button>
              </div>
            )}
          </>
        </div>
      )}
    </DrawerModal>
  );
};
